import React, { useState } from 'react';
import { Modal, Button, Spin, Tooltip, notification } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import ConfirmationIcon from '../../../Assets/Icons/ConfirmationIcon';
import { generateMipsScore, saveMipsScore } from '../../../Apis/Jobs';

const stripHtml = html =>
  (html || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getSectionHeading = s => {
  if (s.heading && typeof s.heading === 'object') {
    return s.heading.customUIHeading || '';
  }
  return s.heading || s.title || '';
};

const getSectionContent = s => {
  if (Array.isArray(s.content)) {
    return s.content
      .filter(c => c.type?.toLowerCase() === 'text')
      .map(c => stripHtml(c.value || ''))
      .join(' ');
  }
  return stripHtml(s.content || s.text || '');
};

const MipsCompleteModal = ({
  visible,
  confirmLoading,
  onConfirm,
  onCancel,
  sections,
  mipsPrompt,
  jobId,
  userId
}) => {
  const [mipsLoading, setMipsLoading] = useState(false);
  const [mipsResult, setMipsResult] = useState(null);

  const handleCancel = () => {
    onCancel();
  };

  const handleGenerateMips = () => {
    const jobSections = (sections || [])
      .map(
        s =>
          `heading:'${getSectionHeading(s)}', content:'${getSectionContent(s)}'`
      )
      .join(', ');

    setMipsLoading(true);
    generateMipsScore(mipsPrompt, jobSections)
      .then(response => {
        const resultData = response.data?.result?.[0] || {};
        const score = resultData.mipsScore || 0;
        const suggestionsList = resultData.scoreImprovementSuggestions || [];
        setMipsResult({ score, suggestions: suggestionsList });

        saveMipsScore({
          jobId,
          userId,
          timestamp: Date.now(),
          score,
          suggestions: suggestionsList.map(s => `-${s}`).join('\n'),
          prompt: `${mipsPrompt} ${jobSections}`
        });
      })
      .catch(() => {
        notification.error({
          message: 'Failed',
          description: 'Unable to generate MIPS score. Please try again.'
        });
      })
      .finally(() => {
        setMipsLoading(false);
      });
  };

  const btnStyle = primary => ({
    fontSize: primary ? 16 : 15,
    fontWeight: 600,
    borderRadius: 7,
    height: 35,
    ...(primary
      ? {}
      : { color: '#8D8989', backgroundColor: '#CFD3D6', border: 'none' })
  });

  const footerButtons = mipsLoading ? null : mipsResult ? (
    <div
      style={{ display: 'flex', justifyContent: 'space-evenly', width: '100%' }}
    >
      <Button
        key='edit'
        type='primary'
        onClick={handleCancel}
        style={btnStyle(true)}
      >
        Edit Job
      </Button>
      <Button
        key='confirm'
        type='primary'
        loading={confirmLoading}
        onClick={onConfirm}
        style={btnStyle(true)}
      >
        Confirm
      </Button>
    </div>
  ) : (
    <div
      style={{ display: 'flex', justifyContent: 'space-evenly', width: '100%' }}
    >
      <Button
        key='mips'
        type='primary'
        onClick={handleGenerateMips}
        style={btnStyle(true)}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          Generate MIPS Score
          <Tooltip title='This score is an estimate based on documented elements and is intended to highlight potential gaps. It is not an official CMS score.'>
            <InfoCircleOutlined
              style={{ fontSize: 14, color: '#555' }}
              onClick={e => e.stopPropagation()}
            />
          </Tooltip>
        </span>
      </Button>
      <Button
        key='confirm'
        type='primary'
        loading={confirmLoading}
        onClick={onConfirm}
        style={btnStyle(true)}
      >
        Confirm
      </Button>
    </div>
  );

  return (
    <Modal
      visible={visible}
      footer={footerButtons}
      closable={true}
      onCancel={handleCancel}
      centered
      destroyOnClose
      width={mipsResult ? 760 : 480}
      bodyStyle={{
        textAlign: 'center',
        padding: mipsResult ? '25px 40px 16px' : '25px 50px 16px',
        fontSize: 16,
        fontWeight: 500
      }}
      afterClose={() => {
        setMipsLoading(false);
        setMipsResult(null);
      }}
    >
      <ConfirmationIcon width='66' height='66' />
      <p style={{ margin: '10px 0' }}>
        Are you sure you want to save this job?
      </p>
      <p style={{ fontSize: '14px', color: '#555', marginTop: 8 }}>
        Submission confirms review and approval of all content, including any
        AI-generated sections.
      </p>

      {mipsLoading && (
        <div
          style={{
            marginTop: 20,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 10
          }}
        >
          <Spin size='large' />
          <span style={{ fontSize: 14, color: '#555' }}>
            Generating score...
          </span>
        </div>
      )}

      {mipsResult && !mipsLoading && (
        <div style={{ marginTop: 16, textAlign: 'left' }}>
          <div
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: '#1890ff',
              marginBottom: 10,
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6
            }}
          >
            {`MIPS Score: ${mipsResult.score}/100`}
            <Tooltip title='This score is an estimate based on documented elements and is intended to highlight potential gaps. It is not an official CMS score.'>
              <InfoCircleOutlined
                style={{ fontSize: 14, color: '#8D8989', cursor: 'pointer' }}
              />
            </Tooltip>
          </div>
          <div
            style={{
              background: '#f5f5f5',
              borderRadius: 8,
              padding: '16px 20px',
              fontSize: 15,
              lineHeight: 1.6,
              color: '#333',
              maxHeight: 420,
              overflowY: 'auto'
            }}
          >
            {(mipsResult.suggestions || []).map((s, i) => (
              <p key={i} style={{ margin: '8px 0' }}>{`-${s}`}</p>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default MipsCompleteModal;
